import 'normalize.css';

import '@kamernet/styles/GlobalStyles.css';
import '@kamernet/styles/theme.design.css';
import '@kamernet/styles/theme.mui.css';

import React from 'react';

import NextApp, {
  AppInitialProps,
  AppContext as NextAppContext,
  AppProps as NextAppProps,
} from 'next/app';
import Head from 'next/head';

import { EmotionCache } from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

import { IncomingMessage } from 'http';

import { v4 as generateUUID } from 'uuid';

import { identify, page, track } from '@hbf/analytics';
import { IntegrationNames } from '@hbf/analytics/lib/constants';

import { AffiliatesSetup } from '@kamernet/core/Affiliates';
import { Hotjar } from '@kamernet/core/Analytics/Hotjar';
import {
  AnalyticsEvent,
  AnalyticsPageCategory,
  AnalyticsPageName,
  AnalyticsPageProps,
  AnalyticsUserProperties,
} from '@kamernet/core/Analytics/types';
import {
  getPageViewedEventPayload,
  getPageEventPayload,
  getUserProperties,
  storeFBCInSuperProperties,
  storeFBPInSuperProperties,
} from '@kamernet/core/Analytics/utilities';
import { AppServicesProvider } from '@kamernet/core/AppServices';
import { AuthProvider } from '@kamernet/core/Auth/AuthProvider';
import {
  GoogleOneTap,
  RecaptchaEnterprise,
} from '@kamernet/core/Auth/components';
import { CommonMessages } from '@kamernet/core/CommonMessages';
import {
  setCustomErrorTag,
  setUserContextForErrorReporting,
} from '@kamernet/core/Errors';
import { BrowserErrorBoundary } from '@kamernet/core/Errors/BrowserErrorBoundary';
import { Error404 } from '@kamernet/core/Errors/Error404';
import { Error500 } from '@kamernet/core/Errors/Error500';
import { CustomTags, ErrorModules } from '@kamernet/core/Errors/types';
import { IntlProvider, createIntl } from '@kamernet/core/Intl';
import { Locale } from '@kamernet/core/Intl/types';
import { createMuiDateLocalizationAdapter } from '@kamernet/core/MUI/MuiDateLocalizationAdapter';
import { PageProvider } from '@kamernet/core/Page';
import { UnhandledErrorPageProps, PageProps } from '@kamernet/core/Page/types';
import { ServiceWorkerSetup } from '@kamernet/core/ServiceWorker';
import { createEmotionCache } from '@kamernet/styles/createEmotionCache';
import { makeTheme } from '@kamernet/styles/theme.mui';

import { FlushAnonymousPSA } from '@kamernet/modules/Search/components/FlushAnonymousPSA';

const clientSideEmotionCache = createEmotionCache();

function sendRudderstackPageEvent(
  analytics: AnalyticsPageProps['analytics'],
  locale: Locale,
  userProperties: AnalyticsUserProperties,
): void {
  const pageEventPayload = getPageEventPayload(
    locale,
    analytics?.properties,
    userProperties,
  );

  page(
    analytics?.category || AnalyticsPageCategory.Other,
    analytics?.name || AnalyticsPageName.Other,
    pageEventPayload,
    {
      integrations: {
        [IntegrationNames.Mixpanel]: false,
      },
    },
  );
}

/**
 * This event is only used for Mixpanel because Kamernet tracks page viewed events as 'PageViewed'. This is why we can't use sendRudderstackPageEvent(), which uses "Loaded a page" instead
 */
function sendMixpanelPageEvent(
  analytics: AnalyticsPageProps['analytics'],
  locale: Locale,
  userProperties: AnalyticsUserProperties,
): void {
  const pageViewedEventPayload = getPageViewedEventPayload(
    locale,
    analytics?.name,
    analytics?.category,
    analytics?.properties,
    userProperties,
  );

  track(AnalyticsEvent.PageViewed, pageViewedEventPayload, {
    integrations: { All: false, [IntegrationNames.Mixpanel]: true },
  });
}

interface AppProps extends NextAppProps<PageProps> {
  emotionCache?: EmotionCache;
}

const App = ({
  Component,
  emotionCache = clientSideEmotionCache,
  pageProps,
  router,
}: AppProps) => {
  const {
    locale,
    translations,
    authState,
    deviceType,
    featureFlags,
    anonymousID,
    targetPageProps,
    hasError404,
    hasError500,
    traceID,
  } = pageProps;

  const intl = React.useMemo(() => {
    return createIntl({
      locale,
      messages: translations,
    });
  }, [translations, locale]);

  const { T } = intl;

  const dateAdapter = React.useMemo(
    () => createMuiDateLocalizationAdapter(),
    [],
  );

  const theme = React.useMemo(
    () => makeTheme(locale, deviceType),
    [deviceType, locale],
  );

  const userId = authState.userProfile?.userid;
  const userMembershipName = authState.userProfile?.productshortdescription;

  React.useEffect(() => {
    storeFBPInSuperProperties();
    storeFBCInSuperProperties();
    const userProperties = getUserProperties(userMembershipName ?? null);

    if (userId) {
      identify(`u${userId}`, userProperties);
    }

    sendMixpanelPageEvent(
      targetPageProps?.analytics,
      locale,
      userId ? {} : userProperties,
    );

    sendRudderstackPageEvent(
      targetPageProps?.analytics,
      locale,
      userId ? {} : userProperties,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.pathname, userId, userMembershipName, locale]);

  React.useEffect(() => {
    setCustomErrorTag(CustomTags.KN_Module, ErrorModules.AppBootstrap);

    if (userId) {
      setUserContextForErrorReporting(
        authState.userProfile?.useremail,
        locale,
        anonymousID,
      );
    }
  }, [anonymousID, authState.userProfile, locale, userId]);

  return (
    <BrowserErrorBoundary T={T} locale={locale} traceID={traceID}>
      <PageProvider pageProps={pageProps} locale={locale}>
        <React.Fragment>
          <Head>
            <meta
              name="viewport"
              content="minimum-scale=1, initial-scale=1, width=device-width"
            />
          </Head>

          <RecaptchaEnterprise />
          <Hotjar />

          <IntlProvider value={intl}>
            <Head key="app">
              <title>{T(CommonMessages.app_name)}</title>
              <meta
                name="apple-mobile-web-app-title"
                content={T(CommonMessages.app_name)}
              />
              <meta
                name="application-name"
                content={T(CommonMessages.app_name)}
              />
            </Head>

            <AppServicesProvider featureFlags={featureFlags}>
              <ServiceWorkerSetup />
              <AffiliatesSetup />
              <AuthProvider authState={authState}>
                <FlushAnonymousPSA />
                {!authState.userProfile && <GoogleOneTap />}
                <CacheProvider value={emotionCache}>
                  <ThemeProvider theme={theme}>
                    <StyledEngineProvider injectFirst>
                      <LocalizationProvider
                        dateAdapter={dateAdapter}
                        adapterLocale={locale}
                      >
                        {hasError404 && <Error404 />}
                        {hasError500 && <Error500 />}
                        {targetPageProps && <Component {...targetPageProps} />}
                      </LocalizationProvider>
                    </StyledEngineProvider>
                  </ThemeProvider>
                </CacheProvider>
              </AuthProvider>
            </AppServicesProvider>
          </IntlProvider>
        </React.Fragment>
      </PageProvider>
    </BrowserErrorBoundary>
  );
};

interface AppWithPossibleUnhandledErrorProps
  extends NextAppProps<PageProps | UnhandledErrorPageProps> {
  emotionCache?: EmotionCache;
}

const AppWithPossibleUnhandledError = ({
  pageProps,
  Component,
  ...rest
}: AppWithPossibleUnhandledErrorProps) => {
  if (pageProps.type === 'UnhandledErrorPageProps') {
    return <Component {...pageProps} />;
  }

  return <App pageProps={pageProps} Component={Component} {...rest} />;
};

AppWithPossibleUnhandledError.getInitialProps = async (
  appContext: NextAppContext,
): Promise<AppInitialProps> => {
  const { req } = appContext.ctx;

  if (req) {
    Object.assign(req, {
      pathname: appContext.router.pathname,
      traceID: req.traceID ? req.traceID : generateUUID(),
    } satisfies Pick<IncomingMessage, 'traceID' | 'pathname'>);
  }

  return NextApp.getInitialProps(appContext);
};

export default AppWithPossibleUnhandledError;
